import React, { useState } from 'react';
import {
  Send, CheckCircle, Coffee, Brain, Gift, Leaf, Mic2, Building2,
  Globe, Users, Palette, Recycle, User, Mail, Phone, Calendar,
  MessageSquare, UsersRound, ChevronRight, Clock, Star, Quote
} from 'lucide-react';
import Navbar from './Components/Navbar';
import Footer from '../Components/Footer';
import './CorporateEventsPage.css';
import { submitEnquiry } from '../lib/enquiries';

const OFFERINGS = [
  {
    Icon: Coffee,
    title: 'Chai Making Workshop',
    desc: 'A hands-on 2-hour karak chai brewing session. Learn ancient spice blending, wellness rituals, and leave with your own personal chai kit.',
    capacity: '10-50 people',
    duration: '2 hours',
  },
  {
    Icon: Brain,
    title: 'Chai & Mindfulness',
    desc: "Chai preparation combined with guided breathing and mindfulness exercises. A full sensory reset for your team's focus and energy.",
    capacity: '10-30 people',
    duration: '2-3 hours',
  },
  {
    Icon: Gift,
    title: 'Gifting & Welcome Boxes',
    desc: 'Bespoke Elaichi & Love gift boxes for your team or clients - curated chai blends, snacks, and wellness extras with your branding.',
    capacity: 'Any size',
    duration: 'Delivery only',
  },
  {
    Icon: Leaf,
    title: 'Wellness Day Experience',
    desc: 'A full-day wellness programme including chai tasting, spice knowledge sessions, nutrition talk, and group activities.',
    capacity: '20-100 people',
    duration: 'Full day',
  },
  {
    Icon: Mic2,
    title: 'Chai Pe Charcha - Corporate',
    desc: 'An open, facilitated conversation over chai. We bring the spice; you bring the topic. Great for leadership offsites and culture-building.',
    capacity: '10-80 people',
    duration: '1-2 hours',
  },
  {
    Icon: Building2,
    title: 'Pop-Up Chai Bar',
    desc: 'Set up an Elaichi & Love chai bar at your office, conference, or event. Live chai preparation, signature blends, branded cups.',
    capacity: '50-500 people',
    duration: 'Half or full day',
  },
];

const WHY_CARDS = [
  { Icon: Globe, title: 'Authentic Roots', desc: 'Real Ayurvedic recipes from Mumbai kitchens - not a wellness gimmick.' },
  { Icon: Users, title: 'Community-First', desc: 'We believe in the power of gathering. Our sessions create genuine connection.' },
  { Icon: Palette, title: 'Fully Bespoke', desc: 'Every experience is tailored to your team, your goals, and your budget.' },
  { Icon: Recycle, title: 'Sustainable', desc: 'Plastic-free, locally sourced, circular kitchen principles in everything we do.' },
];

const TESTIMONIALS = [
  {
    name: 'Claire D.',
    role: 'HR Manager, TechCo Glasgow',
    text: "We did the Chai & Mindfulness session last quarter and the team still talks about it. It was warm, grounding, and so different from anything we've done before.",
    stars: 5,
  },
  {
    name: 'Rohan P.',
    role: 'CEO, Patel & Co.',
    text: 'The Chai Pe Charcha format completely changed how our leadership team communicates. Highly recommend for any company that wants real conversations.',
    stars: 5,
  },
  {
    name: 'Sarah B.',
    role: 'Events Coordinator, City Council',
    text: 'We booked the pop-up chai bar for our community event and it was the centrepiece of the whole day. Elaichi & Love delivered everything perfectly.',
    stars: 5,
  },
];

function CorporateEventsPage() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', eventType: '', size: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;

    setSending(true);
    setSendError('');
    const res = await submitEnquiry({
      type: 'corporate',
      name: form.name,
      email: form.email,
      phone: form.phone,
      subject: `Corporate enquiry - ${form.company}`,
      message: form.message,
      details: {
        company: form.company,
        eventType: form.eventType,
        groupSize: form.size,
      },
    });
    setSending(false);

    // Only show the thank-you once the enquiry has actually reached us.
    if (res.success) setSubmitted(true);
    else setSendError(res.message);
  };

  return (
    <>
      <Navbar />
      <div className="corporate-page">

        {/* Hero */}
        <div className="corporate-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/weeklyritualbox1.jpeg)` }}>
          <div className="corporate-hero-overlay" />
          <div className="corporate-hero-content">
            <p className="corporate-hero-label">FOR ORGANISATIONS & TEAMS</p>
            <h1 className="corporate-hero-title">Bring Elaichi & Love<br/>to Your Workplace</h1>
            <p className="corporate-hero-sub">Team-building chai workshops, wellness days, pop-up bars, and bespoke gifting - rooted in ancient wisdom, delivered with love.</p>
            <a href="#corporate-enquiry" className="corporate-hero-cta">
              Enquire Now <ChevronRight size={18} />
            </a>
          </div>
        </div>

        {/* Why Section */}
        <div className="corporate-why">
          <p className="corporate-section-label">WHY ELAICHI & LOVE</p>
          <h2 className="corporate-section-title">More Than Just Tea</h2>
          <div className="corporate-why-grid">
            {WHY_CARDS.map(({ Icon, title, desc }) => (
              <div key={title} className="corporate-why-card">
                <div className="corporate-why-icon-wrap">
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Offerings */}
        <div className="corporate-offerings">
          <p className="corporate-section-label">WHAT WE OFFER</p>
          <h2 className="corporate-section-title">Corporate Experiences</h2>
          <div className="corporate-offerings-grid">
            {OFFERINGS.map(({ Icon, title, desc, capacity, duration }) => (
              <div key={title} className="corporate-offering-card">
                <div className="corporate-offering-icon-wrap">
                  <Icon size={24} strokeWidth={1.6} />
                </div>
                <h3 className="corporate-offering-title">{title}</h3>
                <p className="corporate-offering-desc">{desc}</p>
                <div className="corporate-offering-meta">
                  <span className="corporate-meta-tag"><UsersRound size={13} /> {capacity}</span>
                  <span className="corporate-meta-tag"><Clock size={13} /> {duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="corporate-testimonials">
          <p className="corporate-section-label">WHAT TEAMS SAY</p>
          <h2 className="corporate-section-title">Trusted by Organisations Across Scotland</h2>
          <div className="corporate-testimonials-grid">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="corporate-testimonial">
                <Quote size={28} className="corporate-testimonial-quote-icon" strokeWidth={1.5} />
                <p className="corporate-testimonial-text">{t.text}</p>
                <div className="corporate-testimonial-footer">
                  <div className="corporate-testimonial-stars">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <Star key={i} size={13} fill="#e8c4a0" color="#e8c4a0" />
                    ))}
                  </div>
                  <div className="corporate-testimonial-author">
                    <span className="corporate-testimonial-name">{t.name}</span>
                    <span className="corporate-testimonial-role">{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="corporate-enquiry" id="corporate-enquiry">
          <div className="corporate-enquiry-inner">
            <div className="corporate-enquiry-info">
              <p className="corporate-section-label">GET IN TOUCH</p>
              <h2 className="corporate-enquiry-title">Plan Your Corporate Event</h2>
              <p className="corporate-enquiry-desc">Tell us about your team and goals - we'll craft a bespoke experience around you. We respond within 24 hours.</p>
              <ul className="corporate-enquiry-checklist">
                <li><CheckCircle size={16} /> Tailored to any team size</li>
                <li><CheckCircle size={16} /> Glasgow & across the UK</li>
                <li><CheckCircle size={16} /> Fully branded options available</li>
                <li><CheckCircle size={16} /> Response within 24 hours</li>
              </ul>
            </div>

            <div className="corporate-enquiry-form-wrap">
              {submitted ? (
                <div className="corporate-success">
                  <CheckCircle size={52} strokeWidth={1.5} className="corporate-success-icon" />
                  <h3>Enquiry Received!</h3>
                  <p>Thank you, {form.name}. We'll be in touch within 24 hours to discuss your event.</p>
                </div>
              ) : (
                <form className="corporate-form" onSubmit={handleSubmit}>
                  <div className="corporate-form-row">
                    <div className="corporate-form-group">
                      <label>Full Name <span>*</span></label>
                      <div className="corporate-input-wrap">
                        <User size={16} className="corporate-input-icon" />
                        <input name="name" value={form.name} onChange={handleChange} placeholder="Your name" required />
                      </div>
                    </div>
                    <div className="corporate-form-group">
                      <label>Company / Organisation <span>*</span></label>
                      <div className="corporate-input-wrap">
                        <Building2 size={16} className="corporate-input-icon" />
                        <input name="company" value={form.company} onChange={handleChange} placeholder="Company name" required />
                      </div>
                    </div>
                  </div>
                  <div className="corporate-form-row">
                    <div className="corporate-form-group">
                      <label>Email Address <span>*</span></label>
                      <div className="corporate-input-wrap">
                        <Mail size={16} className="corporate-input-icon" />
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Work email" required />
                      </div>
                    </div>
                    <div className="corporate-form-group">
                      <label>Phone Number</label>
                      <div className="corporate-input-wrap">
                        <Phone size={16} className="corporate-input-icon" />
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7xxx xxx xxx" />
                      </div>
                    </div>
                  </div>
                  <div className="corporate-form-row">
                    <div className="corporate-form-group">
                      <label>Event Type</label>
                      <div className="corporate-input-wrap">
                        <Calendar size={16} className="corporate-input-icon" />
                        <select name="eventType" value={form.eventType} onChange={handleChange}>
                          <option value="">Select an option</option>
                          {OFFERINGS.map(o => <option key={o.title} value={o.title}>{o.title}</option>)}
                          <option value="Other">Other / Custom</option>
                        </select>
                      </div>
                    </div>
                    <div className="corporate-form-group">
                      <label>Estimated Team Size</label>
                      <div className="corporate-input-wrap">
                        <UsersRound size={16} className="corporate-input-icon" />
                        <input name="size" value={form.size} onChange={handleChange} placeholder="e.g. 25 people" />
                      </div>
                    </div>
                  </div>
                  <div className="corporate-form-group">
                    <label>Tell Us More</label>
                    <div className="corporate-input-wrap corporate-textarea-wrap">
                      <MessageSquare size={16} className="corporate-input-icon corporate-input-icon--top" />
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="Date, location, goals, or any special requests..." rows={4} />
                    </div>
                  </div>
                  {sendError && <p className="formStatus formStatus--error">{sendError}</p>}
                  <button type="submit" className="corporate-submit-btn" disabled={sending}>
                    <Send size={17} /> Send Enquiry
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
}

export default CorporateEventsPage;
